import { useLayoutEffect, useRef, useState } from 'react'
import type { PointerEvent } from 'react'
import { ResponsiveImage } from '../../components/ResponsiveImage/ResponsiveImage'
import { ProtectedImage } from '../../components/ProtectedImage/ProtectedImage'
import { SectionLabel } from '../../components/SectionLabel/SectionLabel'
import { SectionScaffold } from '../../components/SectionScaffold/SectionScaffold'
import { assets } from '../../data/assets'
import { content } from '../../data/content'
import {
  createMotionMedia,
  gsap,
  initializeWhenNear,
  motionConditions,
  type MotionConditionState,
} from '../../motion'
import styles from './TransformationSection.module.css'

const lanternVideo = '/assets/optimized/videos/sec-003-lantern.mp4'
const lanternPoster = '/assets/optimized/posters/sec-003-lantern.webp'
const lanternWechatAnimation = '/assets/animations/sec-003-wechat.webp'

export function TransformationSection() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const visualRef = useRef<HTMLDivElement>(null)
  const afterRef = useRef<HTMLDivElement>(null)
  const sliderRef = useRef<HTMLButtonElement>(null)
  const lanternVideoRef = useRef<HTMLVideoElement>(null)
  const timelineRef = useRef<gsap.core.Timeline | null>(null)
  const draggingRef = useRef(false)
  const [isLanternPlaying, setIsLanternPlaying] = useState(false)
  const [shouldPlayWechatAnimation, setShouldPlayWechatAnimation] =
    useState(false)
  const [isWechatAnimationReady, setIsWechatAnimationReady] = useState(false)
  const [isIosWechat] = useState(() => {
    if (typeof navigator === 'undefined') return false

    const debugIosWechat =
      import.meta.env.DEV &&
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('sec3Media') ===
        'IOS_WECHAT'
    const isWechat = navigator.userAgent.includes('MicroMessenger')
    const isIosDevice =
      /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

    return debugIosWechat || (isWechat && isIosDevice)
  })
  const copy = content['SEC-003']
  const before = assets['AST-002']
  const after = assets['AST-003']
  const isLanternMediaActive = isIosWechat
    ? isWechatAnimationReady
    : isLanternPlaying

  useLayoutEffect(() => {
    const section = sectionRef.current
    const visual = visualRef.current
    const afterLayer = afterRef.current
    const slider = sliderRef.current

    if (!section || !visual || !afterLayer || !slider) {
      return
    }

    return initializeWhenNear(section, () => {
      const media = createMotionMedia()

      media.add(motionConditions, (context) => {
        const { desktop, landscapeMobile, reduceMotion } =
          context.conditions as MotionConditionState
        const canPin = desktop && !landscapeMobile && !reduceMotion

        if (reduceMotion) {
          gsap.set(afterLayer, { clipPath: 'inset(0 0 0 0)' })
          gsap.set(slider, { autoAlpha: 0 })
          return
        }

        const timeline = gsap.timeline({
          scrollTrigger: {
            trigger: visual,
            start: canPin ? 'top top' : 'top 82%',
            end: canPin ? '+=200%' : 'bottom 18%',
            pin: canPin ? visual : false,
            scrub: 1,
            invalidateOnRefresh: true,
            anticipatePin: canPin ? 1 : 0,
          },
        })

        timeline
          .fromTo(
            afterLayer,
            { clipPath: 'polygon(0 0, 0 0, 0 100%, 0 100%)' },
            {
              clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)',
              ease: 'none',
            },
            0,
          )
          .fromTo(slider, { left: '0%' }, { left: '100%', ease: 'none' }, 0)

        timelineRef.current = timeline
      })

      return () => {
        timelineRef.current = null
        media.revert()
      }
    })
  }, [])

  const updateDrag = (clientX: number) => {
    const visual = visualRef.current
    const scrollTrigger = timelineRef.current?.scrollTrigger
    if (!visual || !scrollTrigger) return

    const rect = visual.getBoundingClientRect()
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
    const scrollPosition =
      scrollTrigger.start + (scrollTrigger.end - scrollTrigger.start) * ratio
    window.scrollTo(0, scrollPosition)
  }

  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    draggingRef.current = true
    event.currentTarget.setPointerCapture(event.pointerId)
    updateDrag(event.clientX)
  }

  const handlePointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    if (draggingRef.current) updateDrag(event.clientX)
  }

  const handlePointerEnd = (event: PointerEvent<HTMLButtonElement>) => {
    draggingRef.current = false
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  const handleLanternPlay = () => {
    if (isIosWechat) {
      setShouldPlayWechatAnimation(true)
      return
    }

    const video = lanternVideoRef.current
    if (!video) return

    video.defaultMuted = true
    video.muted = true
    video.loop = true
    video.playsInline = true
    video.setAttribute('muted', '')
    video.setAttribute('loop', '')
    video.setAttribute('playsinline', '')
    video.setAttribute('webkit-playsinline', '')

    void video
      .play()
      .then(() => setIsLanternPlaying(true))
      .catch(() => setIsLanternPlaying(false))
  }

  return (
    <SectionScaffold
      id="SEC-003"
      className={styles.section}
      labelledBy="sec-003-title"
    >
      <div ref={sectionRef} className={styles.motionRoot}>
        <div className={styles.stage}>
          <div className={styles.card}>
            <SectionLabel>— 03 / TRANSFORMATION</SectionLabel>
            <h2 id="sec-003-title" className={styles.title}>
              {copy.title}
            </h2>
            <p className={styles.subtitle}>{copy.subtitle}</p>
            <p className={styles.body}>{copy.body}</p>
          </div>

          <div className={styles.lanternExhibit}>
            <div className={styles.lanternToken}>
              {isIosWechat ? (
                <>
                  <ProtectedImage
                    className={styles.lanternPoster}
                    src={lanternPoster}
                    alt=""
                    aria-hidden="true"
                    decoding="async"
                    draggable={false}
                  />
                  {shouldPlayWechatAnimation ? (
                    <ProtectedImage
                      className={`${styles.lanternWechatAnimation} ${
                        isWechatAnimationReady
                          ? styles.lanternWechatAnimationReady
                          : ''
                      }`}
                      src={lanternWechatAnimation}
                      alt=""
                      aria-hidden="true"
                      decoding="async"
                      draggable={false}
                      onLoad={() => setIsWechatAnimationReady(true)}
                      onError={() => {
                        setShouldPlayWechatAnimation(false)
                        setIsWechatAnimationReady(false)
                      }}
                    />
                  ) : null}
                </>
              ) : (
                <video
                  ref={lanternVideoRef}
                  className={styles.lanternVideo}
                  muted
                  loop
                  playsInline
                  preload="none"
                  poster={lanternPoster}
                  draggable={false}
                  onPlay={() => setIsLanternPlaying(true)}
                  onPause={() => setIsLanternPlaying(false)}
                >
                  <source src={lanternVideo} type="video/mp4" />
                </video>
              )}
              <button
                type="button"
                className={`${styles.lanternPlayButton} ${
                  isLanternMediaActive ? styles.lanternPlayButtonHidden : ''
                }`}
                aria-label="播放新年灯笼设计方案"
                onClick={handleLanternPlay}
              >
                <span className={styles.lanternPlayGlyph} aria-hidden="true" />
              </button>
            </div>
            <div className={styles.lanternCaption}>
              <p className={styles.lanternCaptionTitle}>新年灯笼设计方案</p>
              <p className={styles.lanternCaptionHint}>点击观看</p>
            </div>
          </div>

          <div ref={visualRef} className={styles.visual}>
            <ResponsiveImage
              className={styles.scene}
              src={before.path}
              alt={before.alt}
              width={before.width}
              height={before.height}
              loading="lazy"
              sizes="100vw"
            />

            <div ref={afterRef} className={styles.afterLayer}>
              <ResponsiveImage
                className={styles.scene}
                src={after.path}
                alt={after.alt}
                width={after.width}
                height={after.height}
                loading="lazy"
                sizes="100vw"
              />
            </div>

            <button
              ref={sliderRef}
              type="button"
              className={styles.slider}
              aria-label={copy.compareLabel}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerEnd}
              onPointerCancel={handlePointerEnd}
            >
              <span className={styles.sliderLine} />
              <span className={styles.sliderHandle} />
            </button>

          </div>
        </div>
      </div>
    </SectionScaffold>
  )
}
